var defineProperty = require('core-js/internals/object-define-property').f;
var LISTENERS_KEY = "__core-js_listeners__";
var PASSIVE_KEY = "__code-js_passive__";

// Based on https://github.com/WICG/EventListenerOptions/blob/gh-pages/EventListenerOptions.polyfill.js
// Support both window and worker global scopes

function WrappedEventListener(target, type, listener, options){
  this.target = target;
  this.type = type;
  this.callback = listener;
  this.capture = options.capture;
  this.once = options.once;
  this.passive = options.passive;
}
WrappedEventListener.prototype.handleEvent = function(event){
  // Auto remove (once listener)
  if(this.once){
    this.target.removeEventListener(this.type, this.callback, this.capture);
  }

  // Patch event to ignore if passive has been requested
  event[PASSIVE_KEY] = this.passive;

  if (typeof this.callback === "function") {
    this.callback.call(this.target, event);
  } else if(typeof this.callback === "object" && typeof this.callback.handleEvent === "function") {
    this.callback.handleEvent(event);
  }

  // Restore
  event[PASSIVE_KEY] = undefined;
};

/**
 * Reuse same wrapped listener or create a new one an register it
 * @param {EventTarget} target
 * @param {string} type
 * @param {function|{handleEvent}} callback
 * @param {EventListenerOptions} options
 * @return {WrappedEventListener}
 */
function findWrappedListener(target, type, callback, options){
  var capture = options.capture;
  //var passive = options.passive;
  var list = target[LISTENERS_KEY];

  // https://dom.spec.whatwg.org/#add-an-event-listener 2.7 add an event listener, step 3
  for(var i = 0; list !== undefined && i < list.length; i++){
    var wrappedListener = list[i];

    if(
      wrappedListener.type === type
      && wrappedListener.callback === callback
      && wrappedListener.capture === capture
    ){
      return wrappedListener;
    }
  }

  return null;
}

// https://dom.spec.whatwg.org/#dictdef-addeventlisteneroptions
// AddEventListenerOptions
function EventListenerOptions(target, type, options){
  options = typeof options === "boolean" ? {capture: options} : Object(options);

  this.capture = Boolean(options.capture);
  this.once = Boolean(options.once);
  // default true for passive option for some touch events
  // https://developers.google.com/web/updates/2017/01/scrolling-intervention
  // https://www.chromestatus.com/features/5093566007214080
  // https://github.com/WICG/interventions/issues/18
  // https://github.com/whatwg/dom/issues/365
  // TODO update built-ins browserlist config -> Chrome 56
  var defaultPassive = (
    // Events default passive to true
    type === "touchstart"
    || type === "touchmove"
  ) && (
    // Target default passive to true
    // is Window
    target.window === target
    // is document
    || target.nodeType === 9//Node.DOCUMENT_NODE
    // is body
    || target.nodeName === "BODY"
    // is document.documentElement
    || target.ownerDocument && target === target.ownerDocument.documentElement
  );
  this.passive = "passive" in options ? Boolean(options.passive) : defaultPassive;
}
EventListenerOptions.prototype.getSupportedOptions = function(){
  return optionsUnsupported ? this.capture : this;
};

// Used for both browsers that support useCapture boolean only and others the options object
// Here try to use passive events https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Improving_scrolling_performance_with_passive_listeners
// https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
// https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md#feature-detection
var optionsUnsupported = true;
var passiveUnsupported = true;
var onceUnsupported = true;
try {
  var options = defineProperty({}, name, {
    get capture(){
      // This function will be called when the browser
      // attempts to access the property.
      optionsUnsupported = false;
    },
    get passive(){
      passiveUnsupported = false;
    },
    get once(){
      onceUnsupported = false;
    },
  });

  self.addEventListener(options, options, options);
} catch(err) {
  optionsUnsupported = true;
  passiveUnsupported = true;
  onceUnsupported = true;
}
var overrideNative = optionsUnsupported || passiveUnsupported || onceUnsupported;

function updateProto(proto){
  var originalAddEventListener = proto.addEventListener;
  proto.addEventListener = function (type, listener, options) {
    if(!listener){
      return;
    }

    var listenerOptions = new EventListenerOptions(this, type, options);
    var wrappedListener = findWrappedListener(this, type, listener, listenerOptions);
    // A listener already exist
    if(wrappedListener){
      return;
    }

    wrappedListener = new WrappedEventListener(this, type, listener, listenerOptions);
    var list = this[LISTENERS_KEY] || (this[LISTENERS_KEY] = []);
    list.push(wrappedListener);

    // https://dom.spec.whatwg.org/#add-an-event-listener
    originalAddEventListener.call(this, type, wrappedListener, listenerOptions.getSupportedOptions());
  };

  var originalRemoveEventListener = proto.removeEventListener;
  proto.removeEventListener = function (type, listener, options) {
    if(!listener){
      return;
    }

    var listenerOptions = new EventListenerOptions(this, type, options);
    var wrappedListener = findWrappedListener(this, type, listener, listenerOptions);

    if(!wrappedListener){
      // fallback to listener itself in case of addEventListener has been called before this polyfill been installed
      // that means the wrapper listener mechanism doesn't exist at this time, just allow to remove non wrapped listener
      // just pass through
      originalRemoveEventListener.call(this, type, listener, options);
      return;
    }

    // Remove wrapped listener
    var list = this[LISTENERS_KEY];
    list.splice(list.indexOf(wrappedListener), 1);

    originalRemoveEventListener.call(this, type, wrappedListener, listenerOptions.getSupportedOptions());
  };
}

// See also https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/removeEventListener#Polyfill_to_support_older_browsers
if(overrideNative) {
  // Fix preventDefault to ignore default preventing of passive events
  var originalPreventDefault = Event.prototype.preventDefault;
  Event.prototype.preventDefault = function() {
    if (this.cancelable && this[PASSIVE_KEY]) {
      console.warn("Ignored attempt to preventDefault an event from a passive listener");
      return;
    }
    originalPreventDefault.apply(this);
  };

  // Upgrade EventTarget if it's inherit. Else it's a polyfill, don't upgrade it.
  var hasNativeEventTarget = self.EventTarget && self instanceof EventTarget;
  var eventTargets = hasNativeEventTarget ? [
    EventTarget.prototype,
  ] : [
    // Note: the all following constructors doesn't exist in WorkerGlobalScope
    Text.prototype,
    Element.prototype,
    (self.HTMLDocument || Document).prototype,
    Window.prototype,
    XMLHttpRequest.prototype,
  ];

  eventTargets.forEach(updateProto);
}


